import {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {EMPHASIS} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import {raisedSurfaceShadow} from '../../../styles/elevation';
import {EyeIcon} from './EyeIcon';
import {EyeOffIcon} from './EyeOffIcon';

/** A column as the menu needs to present it. */
export interface VisibilityMenuColumn<K extends string> {
	key: K;
	label: string;
}

interface Props<K extends string> {
	/** The toggleable columns only — mandatory columns have nothing to offer here. */
	columns: readonly VisibilityMenuColumn<K>[];
	isVisible: (key: K) => boolean;
	onToggle: (key: K) => void;
	/**
	 * Viewport width below which the control is hidden, in px. Each table decides
	 * its own: hiding columns is meaningless on a layout that has already dropped
	 * to a narrow presentation.
	 */
	hideBelowPx: number;
	'data-testid'?: string;
}

const Container = styled.div<{$hideBelowPx: number}>`
	position: relative;
	display: block;
	@media (max-width: ${p => p.$hideBelowPx - 1}px) {
		display: none;
	}
`;
// The open-state tint is layered onto the design-system Button itself, so these
// declarations follow the Button's own in the stylesheet and win. A surrounding
// wrapper could not colour the trigger, because the Button always sets its own
// `color`.
const TriggerButton = styled(LabelledButton)<{$open: boolean}>`
	background: ${p => (p.$open ? p.theme.sys.color.background.primary['1'] : 'transparent')};
	color: ${p => (p.$open ? p.theme.sys.color.brand.primary['1'] : p.theme.sys.color.text.content.main)};
`;
const Menu = styled.div`
	position: absolute;
	right: 0;
	z-index: 20;
	margin-top: 4px;
	width: 208px;
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	border: ${p => p.theme.sys.size.borderwidth.thin} solid ${p => p.theme.sys.color.border.decorative};
	background: ${p => p.theme.sys.color.background.neutral['1']};
	padding: ${p => p.theme.sys.spacing[200]} 0;
	${raisedSurfaceShadow}
`;
const MenuHeading = styled.p`
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	margin: 0;
	padding: 0 12px 8px;
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;
const MenuItemRow = styled.div<{$muted?: boolean}>`
	display: flex;
	width: 100%;
	align-items: center;
	justify-content: space-between;
	padding: 6px 12px;
	color: ${p => (p.$muted ? p.theme.sys.color.text.content.auxiliary : p.theme.sys.color.text.content.main)};
	&:hover {
		background: ${p => p.theme.sys.color.background.disabled['1']};
	}
`;
const ItemLabel = styled.span`
	${p => p.theme.sys.font?.body?.['2']}
`;
const VisibleIconWrap = styled.span`
	display: inline-flex;
	color: ${p => p.theme.sys.color.text.content.main};
`;
const HiddenIconWrap = styled.span`
	display: inline-flex;
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;
const Divider = styled.hr`
	margin: 6px 0;
	border: 0;
	border-top: 1px solid ${p => p.theme.sys.color.border.decorative};
`;

/**
 * The eye-icon "show/hide columns" menu, shared by every ticketing table.
 *
 * Visible columns are listed first, then hidden ones below a divider, so the menu
 * doubles as a readout of the current table without the user having to compare it
 * against the header row.
 */
export function ColumnsVisibilityMenu<K extends string>({columns, isVisible, onToggle, hideBelowPx, ...rest}: Props<K>) {
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		function onClickOutside(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
		}
		function onKey(e: KeyboardEvent) {
			if (e.key === 'Escape') setOpen(false);
		}
		document.addEventListener('mousedown', onClickOutside);
		document.addEventListener('keydown', onKey);
		return () => {
			document.removeEventListener('mousedown', onClickOutside);
			document.removeEventListener('keydown', onKey);
		};
	}, [open]);

	const visibleCols = columns.filter(col => isVisible(col.key));
	const hiddenCols = columns.filter(col => !isVisible(col.key));

	return (
		<Container ref={ref} $hideBelowPx={hideBelowPx}>
			<TriggerButton
				$open={open}
				emphasis={EMPHASIS.LOW}
				aria-haspopup="menu"
				aria-expanded={open}
				label="Show/hide columns"
				title="Show/hide columns"
				onPress={() => setOpen(o => !o)}
				data-testid={rest['data-testid']}
			>
				<EyeIcon size={16} />
			</TriggerButton>
			{open && (
				<Menu role="menu">
					<MenuHeading>Columns</MenuHeading>
					{visibleCols.map(col => (
						<LabelledButton
							key={col.key}
							emphasis={EMPHASIS.MEDIUM}
							role="menuitem"
							label={col.label}
							onPress={() => onToggle(col.key)}
						>
							<MenuItemRow>
								<ItemLabel>{col.label}</ItemLabel>
								<VisibleIconWrap>
									<EyeIcon size={16} data-testid="eye-icon" />
								</VisibleIconWrap>
							</MenuItemRow>
						</LabelledButton>
					))}
					{visibleCols.length > 0 && hiddenCols.length > 0 && <Divider role="separator" />}
					{hiddenCols.map(col => (
						<LabelledButton
							key={col.key}
							emphasis={EMPHASIS.MEDIUM}
							role="menuitem"
							label={col.label}
							onPress={() => onToggle(col.key)}
						>
							<MenuItemRow $muted>
								<ItemLabel>{col.label}</ItemLabel>
								<HiddenIconWrap>
									<EyeOffIcon size={16} data-testid="eye-off-icon" />
								</HiddenIconWrap>
							</MenuItemRow>
						</LabelledButton>
					))}
				</Menu>
			)}
		</Container>
	);
}
